/**
 * AI Assistant Feature Constants
 * Ported from Angular ai-capabilities.config.ts and loading-messages.service.ts
 */

import type { AiCapability } from './types';

// =============================================================================
// ABP FEATURE FLAGS
// =============================================================================

export const ABP_FEATURES = {
  AI_CAPABILITIES_ENABLED: 'motr.PremiumFeatures.AiCapabilities.Enable',
} as const;

// =============================================================================
// AI CAPABILITIES
// =============================================================================

export const AI_CAPABILITIES: AiCapability[] = [
  {
    title: 'Job Management',
    shortDescription: 'Manage job statuses, details, and tasks efficiently',
    detailedPrompt:
      'Can you provide detailed information about job management? Show me what job-related tasks you can help me with, including checking job statuses, viewing job details, and managing job workflows.',
  },
  {
    title: 'Document Retrieval',
    shortDescription: 'Fetch and search through job-related documents',
    detailedPrompt:
      'How can you help me with document retrieval? Show me the types of documents you can fetch, search capabilities, and how to find specific documents related to my jobs.',
  },
  {
    title: 'Data Analysis',
    shortDescription: 'Generate charts and reports from job data',
    detailedPrompt:
      'What kind of data analysis and reporting can you do? Show me examples of charts, reports, and visualizations you can create from job data.',
  },
  {
    title: 'Template Creation',
    shortDescription: 'Create and manage workflow templates',
    detailedPrompt:
      'How can you assist with template creation? Explain the process of creating workflow templates, what options are available, and how to save and reuse templates.',
  },
  {
    title: 'File Search',
    shortDescription: 'Search through uploaded documents quickly',
    detailedPrompt:
      'Tell me about your file search capabilities. How can you help me find specific information in uploaded documents? What search features are available?',
  },
  {
    title: 'Email Monitoring',
    shortDescription: 'Monitor emails and create jobs automatically',
    detailedPrompt:
      'How does email monitoring work? Explain how you can monitor incoming emails, parse their content, and automatically create jobs from email information.',
  },
  {
    title: 'Workflow Automation',
    shortDescription: 'Automate repetitive workflow tasks',
    detailedPrompt:
      'What workflow automation features do you offer? Show me how you can help automate repetitive tasks, set up automated workflows, and improve efficiency.',
  },
  {
    title: 'Client & Matter Management',
    shortDescription: 'Manage clients and matter information',
    detailedPrompt:
      'How can you help with client and matter management? Show me what information you can provide about clients, matters, and how to manage them effectively.',
  },
];

// =============================================================================
// LOADING MESSAGES
// =============================================================================

export const CHAT_LOADING_MESSAGES: string[] = [
  'Teaching artificial neurons to do synchronized swimming...',
  'Defragmenting binary dreams and digital aspirations...',
  'Consulting my silicon-based crystal ball...',
  'Converting caffeine into code... please wait...',
  'Performing quantum calculations on a abacus...',
  'Downloading more RAM from the cloud... (just kidding)',
  'Practicing my robot dance moves while processing...',
  'Teaching AI the concept of Monday mornings...',
  'Polishing neural pathways for maximum sparkle...',
  'Counting electric sheep for inspiration...',
  'Organizing ones and zeros in alphabetical order...',
  'Convincing algorithms to play nice with each other...',
  'Running on a hamster wheel to generate more computing power...',
  'Untangling the web of infinite possibilities...',
  'Debugging the meaning of life (currently at 41)...',
  'Reticulating splines and contemplating existence...',
  'Teaching machine learning to appreciate dad jokes...',
  'Calibrating the flux capacitor for optimal performance...',
  'Consulting with digital fortune cookies...',
  'Feeding treats to the code gremlins for faster processing...',
];

// =============================================================================
// ROTATING PLACEHOLDER MESSAGES
// =============================================================================

export const ROTATING_PLACEHOLDERS: string[] = [
  'Ask about job statuses and workflows...',
  'Search through your documents...',
  'Help me create a new template...',
  'Analyze my job data and create reports...',
  'Find information in uploaded files...',
  'How can I automate my workflow?',
  'Show me client and matter details...',
  'What emails need attention today?',
];

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get a random subset of capabilities for display on the welcome screen.
 * Uses crypto.getRandomValues for non-security-critical UI randomization.
 */
export function getRandomCapabilities(count = 4): AiCapability[] {
  const shuffled = [...AI_CAPABILITIES];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    const j = array[0] % (i + 1);
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, Math.min(count, shuffled.length));
}

/**
 * Get a random loading message.
 * Uses crypto.getRandomValues for non-security-critical UI randomization.
 */
export function getRandomLoadingMessage(): string {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  const index = array[0] % CHAT_LOADING_MESSAGES.length;
  return CHAT_LOADING_MESSAGES[index];
}
